import {
  FieldValues,
  Control,
  Path,
  ControllerProps,
  Controller as RHFController,
} from 'react-hook-form';

import { TextField, ITextField } from '../../textField';
import { Switcher, ISwitcher } from '../../switcher';
import { Autocomplete, IAutocomplete } from '../../autocomplete';
import { Checkbox, ICheckbox } from '../../checkbox';
import { RadioGroup, IRadioGroup } from '../../radioButton';
import { TFunction } from 'i18next';

type TTextType = 'textField';
type TPasswordType = 'passwordField';
type TSwitcherType = 'switcherField';
type TAutocompleteType = 'autocompleteField';
type TRadioGroupType = 'radioGroupField';
type TCheckboxType = 'checkboxField';

type TCustomChangeValue = string | number | Date | Array<string> | boolean;

interface ICommonProps<TFormValues extends FieldValues> {
  control: Control<TFormValues>;
  name: Path<TFormValues>;
  onCustomChange?: (value: TCustomChangeValue, name: string) => void;
  onTranslateErrors?: TFunction<'en', undefined>;
}

interface IText<TFormValues extends FieldValues>
  extends Omit<ITextField, 'name'>,
    ICommonProps<TFormValues> {
  type: TTextType;
}

interface IPassword<TFormValues extends FieldValues>
  extends Omit<ITextField, 'name'>,
    ICommonProps<TFormValues> {
  type: TPasswordType;
}

interface ISwitcherProps<TFormValues extends FieldValues>
  extends Omit<ISwitcher, 'name'>,
    ICommonProps<TFormValues> {
  type: TSwitcherType;
}

interface IAutocompleteProps<TFormValues extends FieldValues>
  extends Omit<IAutocomplete, 'name'>,
    ICommonProps<TFormValues> {
  type: TAutocompleteType;
}

interface IRadioGroupProps<TFormValues extends FieldValues>
  extends Omit<IRadioGroup, 'name'>,
    ICommonProps<TFormValues> {
  type: TRadioGroupType;
}

interface ICheckboxProps<TFormValues extends FieldValues>
  extends Omit<ICheckbox, 'name'>,
    ICommonProps<TFormValues> {
  type: TCheckboxType;
}

type TFormFiled<T extends FieldValues> =
  | IText<T>
  | IPassword<T>
  | ISwitcherProps<T>
  | IAutocompleteProps<T>
  | IRadioGroupProps<T>
  | ICheckboxProps<T>;

export const Controller = <TFormValues extends FieldValues>({
  type,
  control,
  onCustomChange,
  onTranslateErrors,
  ...rest
}: TFormFiled<TFormValues>) => {
  const render: ControllerProps<TFormValues, Path<TFormValues>>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const fieldProps = {
      ...field,
      helperText:
        rest.helperText ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (onTranslateErrors && onTranslateErrors(error?.message as any)) ||
        error?.message,
      isError: !!error?.message,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (event: any) => {
      onCustomChange && onCustomChange(event.target.value, event.target.name);
      field.onChange(event);
    };

    switch (type) {
      case 'switcherField':
        return (
          <Switcher
            {...(rest as ISwitcher)}
            {...fieldProps}
            onChange={handleChange}
          />
        );
      case 'passwordField':
        return (
          <TextField
            {...(rest as ITextField)}
            {...fieldProps}
            type='password'
            onChange={handleChange}
          />
        );
      case 'autocompleteField':
        return (
          <Autocomplete
            {...(rest as IAutocomplete)}
            {...fieldProps}
            onChange={handleChange}
          />
        );
      case 'radioGroupField':
        return (
          <RadioGroup
            {...(rest as IRadioGroup)}
            {...fieldProps}
            onChange={handleChange}
          />
        );
      case 'checkboxField':
        return (
          <Checkbox
            {...(rest as ICheckbox)}
            {...fieldProps}
            onChange={handleChange}
          />
        );
      case 'textField':
      default:
        return (
          <TextField
            {...(rest as ITextField)}
            {...fieldProps}
            onChange={handleChange}
          />
        );
    }
  };

  return (
    <RHFController<TFormValues, Path<TFormValues>>
      control={control}
      name={rest.name}
      render={render}
    />
  );
};
